import { z } from "zod";

/**
 * Zemin etüt / geoteknik raporundan yapay zekâ ile (Gemini) çıkarılan parametreler.
 * Birimler sabittir: derinlik m (zemin yüzeyinden aşağı), γ kN/m³, φ derece, c ve c_u kPa (= kN/m²), taşıma gücü kPa, ivmeler g.
 * Bulunamayan değer null; her blokta kaynak sayfa (`page`) ve güven (`confidence`).
 */
const num = z.number().finite().nullable();
const str = z.string().max(400).nullable();
const conf = z.enum(["low", "medium", "high"]).nullable();

export const ExtractedLayerSchema = z.object({
  name: str, description: str,
  depthTop: num, depthBottom: num,
  gamma: num, gammaSat: num, phi: num, c: num, cu: num, sptN: num,
  page: num,
});
export const SoilExtractionSchema = z.object({
  document: z.object({ title: str, firm: str, date: str, pageCount: num, language: str }),
  site: z.object({ city: str, district: str, block: str, parcel: str }),
  layers: z.array(ExtractedLayerSchema).max(30),
  groundwater: z.object({ depth: num, note: str, page: num, confidence: conf }),
  foundation: z.object({
    recommendedDepth: num, bearingLayerName: str,
    allowableBearing: num, ultimateBearing: num,
    baseFrictionAngle: num, frictionCoefficient: num,
    note: str, page: num, confidence: conf,
  }),
  seismic: z.object({ siteClass: str, SS: num, S1: num, SDS: num, SD1: num, PGA: num, designLevel: str, page: num, confidence: conf }),
  backfill: z.object({ gamma: num, phi: num, c: num, note: str, page: num, confidence: conf }),
  warnings: z.array(z.string().max(500)).max(20),
});
export type SoilExtraction = z.infer<typeof SoilExtractionSchema>;
export type ExtractedLayer = z.infer<typeof ExtractedLayerSchema>;

/** İstek: PDF (ya da tek sayfa görsel) base64; ≤ 20 MB ham (Gemini satır içi sınırı). */
export const SOIL_EXTRACT_MAX_BYTES = 20 * 1024 * 1024;
export const SoilExtractRequestSchema = z.object({
  filename: z.string().max(200),
  mime: z.enum(["application/pdf", "image/png", "image/jpeg", "image/webp"]),
  dataBase64: z.string().min(16).max(Math.ceil((SOIL_EXTRACT_MAX_BYTES * 4) / 3) + 16),
  /** Kullanıcı ipucu (ör. "SK-3 sondajı, 2. tabaka") — isteme eklenir. */
  hint: z.string().max(500).optional(),
});
export type SoilExtractRequest = z.infer<typeof SoilExtractRequestSchema>;

export interface SoilExtractResponse { extraction: SoilExtraction; model: string; usage: { promptTokens: number; outputTokens: number }; elapsedMs: number }
export interface ExtractCapabilities { enabled: boolean; model: string | null; maxBytes: number; perHour: number }
